import { useMemo, useState } from "react";

import PageHeader from "../../../components/ui/pageHeader/PageHeader";
import PageCard from "../../../components/ui/pageCard/PageCard";

import FieldRenderer from "../../../components/dynamicFields/FieldRenderer";

import "./FieldPlaygroundPage.css";

/**
 * MOCK FIELD CONFIGS
 *
 * THIS PAGE EXISTS ONLY
 * TO TEST THE RENDERER SYSTEM
 */
const mockFields = [
  {
    key: "first_name",
    label: "Voornaam",
    type: "text",
    placeholder: "Geef een voornaam in",
    required: true,
    width: "half",
    help_text: "Verplicht veld",
    section: "persoonlijk",
    order: 1,
    is_active: true,
  },

  {
    key: "last_name",
    label: "Achternaam",
    type: "text",
    placeholder: "Geef een achternaam in",
    required: true,
    width: "half",
    section: "persoonlijk",
    order: 2,
    is_active: true,
  },

  {
    key: "email",
    label: "E-mailadres",
    type: "email",
    placeholder: "naam@voorbeeld.be",
    width: "half",
    required: true,
    section: "persoonlijk",
    order: 3,
    is_active: true,
  },

  {
    key: "birth_date",
    label: "Geboortedatum",
    type: "date",
    width: "half",
    section: "persoonlijk",
    order: 4,
    is_active: true,
  },

  {
    key: "notes",
    label: "Opmerkingen",
    type: "textarea",
    placeholder: "Extra informatie...",
    width: "full",
    section: "extra",
    order: 5,
    is_active: true,
  },

  {
    key: "vdab_status",
    label: "VDAB Status",
    type: "select",
    width: "half",
    section: "vdab",
    order: 6,
    options: [
      {
        label: "Ingeschreven",
        value: "ingeschreven",
      },

      {
        label: "Niet ingeschreven",
        value: "niet_ingeschreven",
      },
    ],
    is_active: true,
  },

  {
    key: "priority",
    label: "Prioriteit",
    type: "radio",
    width: "half",
    section: "extra",
    order: 7,
    options: [
      {
        label: "Laag",
        value: "laag",
      },

      {
        label: "Normaal",
        value: "normaal",
      },

      {
        label: "Hoog",
        value: "hoog",
      },
    ],
    is_active: true,
  },

  {
    key: "needs_followup",
    label: "Nazorg nodig",
    type: "checkbox",
    width: "full",
    section: "extra",
    order: 8,
    is_active: true,
  },

  {
    key: "interests",
    label: "Interesses",
    type: "multiselect",
    width: "full",
    section: "extra",
    order: 9,
    options: [
      {
        label: "Technologie",
        value: "tech",
      },

      {
        label: "Sport",
        value: "sport",
      },

      {
        label: "Muziek",
        value: "music",
      },

      {
        label: "Gaming",
        value: "gaming",
      },
    ],
    is_active: true,
  },

  {
    key: "document",
    label: "Document upload",
    type: "file",
    width: "full",
    section: "bestanden",
    order: 10,
    help_text: "PDF of afbeelding",
    is_active: true,
  },
];

export default function FieldPlaygroundPage() {
  /**
   * FORM STATE
   */
  const [formData, setFormData] = useState({});

  /**
   * ERRORS
   */
  const [errors, setErrors] = useState({});

  /**
   * GROUPED SECTIONS
   */
  const groupedSections = useMemo(() => {
    const grouped = {};

    mockFields.forEach((field) => {
      const section = field.section || "general";

      if (!grouped[section]) {
        grouped[section] = [];
      }

      grouped[section].push(field);
    });

    /**
     * SORT BY ORDER
     */
    Object.keys(grouped).forEach((sectionKey) => {
      grouped[sectionKey].sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    return grouped;
  }, []);

  /**
   * HANDLE CHANGE
   */
  const handleChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,

      [key]: value,
    }));

    /**
     * CLEAR ERROR
     */
    if (errors[key]) {
      setErrors((prev) => ({
        ...prev,

        [key]: null,
      }));
    }
  };

  /**
   * VALIDATION TEST
   */
  const validate = () => {
    const nextErrors = {};

    mockFields.forEach((field) => {
      const value = formData[field.key];

      /**
       * REQUIRED
       */
      if (field.required) {
        const isEmpty =
          value === undefined ||
          value === null ||
          value === "" ||
          (Array.isArray(value) && value.length === 0);

        if (isEmpty) {
          nextErrors[field.key] = "Dit veld is verplicht.";
        }
      }
    });

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  /**
   * SUBMIT TEST
   */
  const handleSubmit = (e) => {
    e.preventDefault();

    const isValid = validate();

    if (!isValid) return;

    console.log("FORM DATA:", formData);

    alert("Renderer validatie succesvol. Bekijk console.");
  };

  return (
    <div className="field-playground-page">
      {/* PAGE HEADER */}
      <PageHeader
        eyebrow="POWER"
        title="Field Renderer Playground"
        description="Testomgeving voor het dynamische field renderer systeem."
      />

      {/* FORM */}
      <form onSubmit={handleSubmit}>
        {Object.entries(groupedSections).map(([sectionKey, fields]) => (
          <PageCard key={sectionKey} className="field-playground-page__section">
            {/* SECTION HEADER */}
            <div className="field-playground-page__section-header">
              <h2>{sectionKey}</h2>

              <span>{fields.length} velden</span>
            </div>

            {/* SECTION FIELDS */}
            <div className="field-playground-page__fields">
              {fields.map((field) => (
                <FieldRenderer
                  key={field.key}
                  field={field}
                  value={formData[field.key]}
                  error={errors[field.key]}
                  onChange={handleChange}
                />
              ))}
            </div>
          </PageCard>
        ))}

        {/* DEBUG */}
        <PageCard className="field-playground-page__debug">
          <div className="field-playground-page__debug-header">
            <h3>Live Form State</h3>
          </div>

          <pre>{JSON.stringify(formData, null, 2)}</pre>
        </PageCard>

        {/* ACTIONS */}
        <div className="field-playground-page__actions">
          <button type="submit">Renderer testen</button>
        </div>
      </form>
    </div>
  );
}
